import { useMemo, useState } from 'react';
import type { TagEdges, TagId, TagMap } from '../../lib/types';
import { isTagVisible, tagChildren, tagMatchSet, tagRoots } from '../../lib/tags';

/**
 * THE NODE POPUP'S TAG PICKER (V0.51) — the vocabulary, with its hierarchy, in columns.
 *
 * WHAT IT REPLACES, and why the user called it a hot mess. `#pp-tags` rendered
 * `Object.values(s.tags)` as one flex-wrapped wall of pills: every tag in the installation, on
 * every popup, in the server's key order, with no indication that `Attribute` sits under
 * `Entity` under `PoSt` under `MDM`. Three things were wrong with it at once. The hierarchy was
 * invisible, so finding the tag you wanted meant reading the whole wall. The order was
 * meaningless — not even the sidebar's `order`, which is the one somebody chose. And what the
 * node ALREADY carries was scattered through the vocabulary instead of being stated.
 *
 * SO THE SECTION IS TWO THINGS, and the border between them is the point:
 *
 *  - WHAT THIS NODE CARRIES, first, as a strip of filled chips under "Assigned · n". Each has a
 *    × that removes it. This is the answer to "what is on this node", and it was previously
 *    something you worked out by scanning for the lit pills.
 *  - WHAT IT COULD CARRY, in a bordered box of its own under "All tags · N" — the tree, one row
 *    per tag, indented by depth, flowing down CSS columns so a tall vocabulary does not become
 *    a tall popup. With a search that filters it.
 *
 * THE ROW IS THE SIDEBAR'S ROW, wearing `.chip`. `TagTree.tsx` has drawn tags as indented rows
 * with a caret and a colour dot since the app had a sidebar, and a second visual language for
 * the same objects in the same app is a second thing to learn. It keeps the `.chip` / `.on` /
 * `.cd` classes because that is what a selected tag IS here and what the suite finds it by.
 *
 * THE CARETS ARE LOCAL, AND THAT IS LOAD-BEARING. `tag.collapsed` syncs to the server — one
 * person collapsing a group collapses it for everybody (see `lib/tags.ts`). A fold in a picker
 * is a reading convenience for one person in one popup, so it lives in `useState` here and
 * touches nothing. When a folded group hides a tag the node carries, the parent row says `+n`
 * in that tag's colour, so folding can never hide an assignment.
 *
 * PARENTS ARE PICKABLE, deliberately. The data assigns them directly — "Data Modeller" carries
 * `MDM`, this node carries `PoSt` — so treating a parent as a heading would orphan assignments
 * that already exist. Worth knowing, and NOT the same rule: the map's tag FILTER treats a
 * parent as its whole subtree (`nodeHasActiveTag` in `lib/search.ts`), while assignment does
 * not imply the parent. Two different questions; the picker answers only the second.
 *
 * INVISIBLE TAGS follow `isTagVisible` here, as the sidebar does. The old wall showed them to
 * anyone with `ASSIGN_TAGS` regardless of edit mode, which was an accident of never asking:
 * `TagTree` and `ReadTags` both hide them. One rule now. A tag the node already carries stays
 * on the strip whatever the rule says — withholding an assignment because it is awkward to
 * display is how somebody saves a node without noticing what left it.
 */

export interface TagPickerProps {
  tags: TagMap;
  tagEdges: TagEdges;
  /** The DRAFT — what the popup will save, not what the node stores. */
  selected: ReadonlySet<TagId>;
  onToggle: (id: TagId) => void;
  /** Permission-hidden tags, already expanded through the tree by `buildHiddenSets`. */
  hiddenTags: ReadonlySet<TagId>;
  editing: boolean;
  canEditTags: boolean;
}

export function TagPicker({
  tags,
  tagEdges,
  selected,
  onToggle,
  hiddenTags,
  editing,
  canEditTags,
}: TagPickerProps) {
  /** Folded groups, and the query. Per popup, per person, gone on close — never `tag.collapsed`. */
  const [folded, setFolded] = useState<ReadonlySet<TagId>>(() => new Set());
  const [query, setQuery] = useState('');

  const match = useMemo(() => tagMatchSet(tags, tagEdges, query), [tags, tagEdges, query]);

  /** Drawn at all? Hidden tags are gone; an invisible one stays if this node carries it. */
  const shown = (id: TagId): boolean =>
    isTagVisible(tags[id], id, { hiddenTags, editing, canEdit: canEditTags }) || selected.has(id);

  const roots = tagRoots(tags, tagEdges).filter((id) => !hiddenTags.has(id));
  const visibleCount = Object.keys(tags).filter(shown).length;
  const mine = Object.keys(tags).filter((id) => selected.has(id));

  const toggleFold = (id: TagId) => {
    const next = new Set(folded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFolded(next);
  };

  /** One row. `.chip.row` so `.chip`/`.on`/`.cd` still mean what they always did. */
  const row = (id: TagId, depth: number) => {
    const t = tags[id];
    const on = selected.has(id);
    const kids = tagChildren(tags, tagEdges, id).filter(shown);
    const shut = folded.has(id) && !match;
    // A fold must never hide an assignment without saying so.
    const hiddenPicked = shut
      ? kids.flatMap((k) => [k, ...tagChildren(tags, tagEdges, k)]).filter((k) => selected.has(k))
      : [];
    return (
      <div className="trow" style={{ '--d': depth } as React.CSSProperties} key={id}>
        <span
          className={`chip row${on ? ' on' : ''}`}
          style={on ? { background: t.color } : undefined}
          data-tag={id}
          role="button"
          tabIndex={0}
          aria-pressed={on}
          onClick={() => onToggle(id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle(id);
            }
          }}
        >
          {kids.length && !match ? (
            <button
              type="button"
              className="tcaret"
              data-caret={id}
              aria-label={shut ? `Show tags under ${t.name}` : `Hide tags under ${t.name}`}
              aria-expanded={!shut}
              /* The caret is INSIDE the chip and must not toggle it: two targets, one row. */
              onClick={(e) => {
                e.stopPropagation();
                toggleFold(id);
              }}
            >
              {shut ? '▸' : '▾'}
            </button>
          ) : (
            <span className="tcaret" />
          )}
          <span className="cd" style={{ background: t.color }} />
          <span className="nm">{t.name}</span>
          {hiddenPicked.length ? (
            <span
              className="tag-plus"
              style={{ background: tags[hiddenPicked[0]].color }}
              title={hiddenPicked.map((k) => tags[k].name).join(', ')}
            >
              +{hiddenPicked.length}
            </span>
          ) : null}
          {on ? <RemoveX name={t.name} onRemove={() => onToggle(id)} /> : null}
        </span>
      </div>
    );
  };

  /** One root and everything under it, as one unbreakable column block. */
  const group = (rootId: TagId) => {
    const rows: React.ReactNode[] = [];
    const walk = (id: TagId, depth: number) => {
      if (!shown(id)) return;
      if (match && !match.has(id)) return;
      rows.push(row(id, depth));
      if (folded.has(id) && !match) return;
      for (const k of tagChildren(tags, tagEdges, id)) walk(k, depth + 1);
    };
    walk(rootId, 0);
    if (!rows.length) return null;
    return (
      <div className="taggroup" key={rootId}>
        {rows}
      </div>
    );
  };

  const groups = roots.map(group).filter(Boolean);

  return (
    <div id="pp-tags">
      <div className="tagcap">
        Assigned <span className="n">· {mine.length}</span>
      </div>
      {mine.length ? (
        <div className="tagstrip">
          {mine.map((id) => (
            /* The chip BODY removes, and the × removes. Not a redundancy: clicking a lit chip
               to take it off is the gesture the old wall had and people already know, and the ×
               is what makes it discoverable — nothing about a filled pill says it can be
               un-filled. Keeping the body inert would have left a chip whose only live pixel
               was 8px wide. */
            <span
              className="chip on"
              key={id}
              style={{ background: tags[id].color }}
              data-tag={id}
              role="button"
              tabIndex={0}
              aria-pressed
              title={`Remove ${tags[id].name}`}
              onClick={() => onToggle(id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle(id);
                }
              }}
            >
              <span className="cd" style={{ background: tags[id].color }} />
              {tags[id].name}
              <RemoveX name={tags[id].name} onRemove={() => onToggle(id)} />
            </span>
          ))}
        </div>
      ) : (
        <div className="tagstrip-empty">No tags on this node yet.</div>
      )}

      <div className="tagvocab">
        <div className="tagvocab-head">
          <div className="tagcap">
            All tags <span className="n">· {visibleCount}</span>
          </div>
          <label className="tagsearch">
            <svg className="ico" aria-hidden="true">
              <use href="#i-search" />
            </svg>
            <input
              id="pp-tagfilter"
              value={query}
              placeholder="Search tags…"
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
              /* Escape clears the field rather than closing the popup, but ONLY while there is
                 something to clear — otherwise it would swallow the reader's way out. */
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.stopPropagation();
                  setQuery('');
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          </label>
        </div>
        {!groups.length ? (
          <div className="tagvocab-empty">
            {match ? `No tag matches “${query.trim()}”.` : 'No tags yet — add some in the tag panel.'}
          </div>
        ) : (
          <div className="tagpick cols">{groups}</div>
        )}
        {match ? <div className="tagnote">Matches, with the groups they sit in.</div> : null}
      </div>
    </div>
  );
}

/**
 * The × on a selected tag (V0.51) — on the strip and on a lit row.
 *
 * A `<button>` that STOPS PROPAGATION, which is the whole design: the chip body still toggles,
 * so `e2e/36`'s "click the chip with this text" keeps meaning what it meant, and the × is a
 * second target for the one gesture people actually wanted — take this off. `.subj-tok .x`'s
 * manners: faint, red on hover, and white on a chip already filled with the tag's colour.
 */
function RemoveX({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <button
      type="button"
      className="x"
      aria-label={`Remove ${name}`}
      title={`Remove ${name}`}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
    >
      ×
    </button>
  );
}
